import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, IconButton, ButtonBase, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  X,
  FileText,
  Video,
  Ticket,
  MessageSquare,
  Building2,
  ListChecks,
  FolderKanban,
  PenLine,
  BarChart3,
  ExternalLink,
  ChevronRight,
} from "lucide-react";

// Cada fuente que devuelve una tool MCP (bloque SOURCES_METADATA). `doc` y
// `video` vienen de la base de conocimiento (helpdesk, Notion); el resto son
// registros de la propia app y enlazan a su pantalla.
const SOURCE_TYPES = {
  doc: { icon: FileText, label: "Documento" },
  video: { icon: Video, label: "Vídeo" },
  ticket: { icon: Ticket, label: "Ticket" },
  message: { icon: MessageSquare, label: "Conversación" },
  client: { icon: Building2, label: "Cliente" },
  task: { icon: ListChecks, label: "Tarea" },
  project: { icon: FolderKanban, label: "Proyecto" },
  content: { icon: PenLine, label: "Contenido" },
  report: { icon: BarChart3, label: "Informe" },
};

const DEFAULT_SOURCE_TYPE = { icon: FileText, label: "Fuente" };
const KNOWLEDGE_TYPES = new Set(["doc", "video"]);

// Registros que se enseñan antes de "Ver N más": una lista de tareas puede
// citar 20 de golpe.
const RECORDS_PREVIEW = 5;

// Qué hace cada tool: `running` mientras se genera, `done` en el chip bajo la respuesta.
const TOOL_LABELS = {
  semantic_search: { running: "Buscando en la documentación", done: "Buscó en documentación" },
  retrieve_full_document: { running: "Leyendo un documento", done: "Leyó un documento" },
  me: { running: "Consultando tu perfil", done: "Consultó tu perfil" },
  copilot_get_activity_summary: { running: "Revisando tu actividad", done: "Revisó la actividad" },
  list_my_clients: { running: "Buscando el cliente", done: "Buscó el cliente" },
  get_client_context: { running: "Consultando el cliente", done: "Consultó el cliente" },
  list_tickets: { running: "Consultando tickets", done: "Consultó tickets" },
  list_messages: { running: "Leyendo mensajes", done: "Leyó mensajes" },
  list_tasks: { running: "Consultando tareas", done: "Consultó tareas" },
  list_content_pieces: { running: "Consultando contenidos", done: "Consultó contenidos" },
  list_pending_reports: { running: "Consultando informes", done: "Consultó informes" },
  get_project_detail: { running: "Consultando el proyecto", done: "Consultó el proyecto" },
  get_conversation: { running: "Leyendo una conversación", done: "Leyó una conversación" },
};

export const getSourceType = (source) =>
  SOURCE_TYPES[source?.source] || DEFAULT_SOURCE_TYPE;

export const isExternalUrl = (url) => /^https?:\/\//i.test(url || "");

/** Etiquetas distintas de las tools llamadas, en orden de uso. */
export const getToolLabels = (toolsCalled = []) => {
  const labels = toolsCalled.map(
    (tool) => TOOL_LABELS[tool?.name]?.done || "Consultó datos"
  );
  return [...new Set(labels)];
};

/** Tool en curso (llamada sin resultado todavía), o null. */
export const getRunningToolLabel = ({ tools_called = [], tool_results = [] }) => {
  if (tools_called.length <= tool_results.length) return null;
  return TOOL_LABELS[tools_called.at(-1).name]?.running || "Consultando datos";
};

// Los títulos del helpdesk llegan escapados por WordPress ("&#8211;"). Un
// textarea decodifica entidades sin interpretar HTML.
const decodeEntities = (text) => {
  if (!text || !text.includes("&")) return text || "";
  const el = document.createElement("textarea");
  el.innerHTML = text;
  return el.value;
};

// "[HD:964] Embudo de Ventas" -> { title: "Embudo de Ventas", ref: "HD-964" }
const splitTitle = (rawTitle) => {
  const title = decodeEntities(rawTitle).trim();
  const match = title.match(/^\[([A-Z]+):(\d+)\]\s*(.*)$/);
  if (!match) return { title, ref: null };
  return { title: match[3] || title, ref: `${match[1]}-${match[2]}` };
};

const getHostLabel = (url) => {
  if (!isExternalUrl(url)) return "Markezy";
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    if (/(^|\.)youtu(\.be|be\.com)$/.test(host)) return "YouTube";
    if (/(^|\.)notion\.(so|site)$/.test(host)) return "Notion";
    return host;
  } catch {
    return url;
  }
};

/**
 * Agrupa las fuentes de un mensaje para pintarlas.
 *
 * El RAG cita el mismo documento una vez por chunk recuperado y dos tools
 * pueden citar el mismo registro, así que se deduplica por (source, source_id).
 * La documentación se ordena por relevancia (mejor score del documento); los
 * registros conservan el orden en que llegaron.
 */
export const groupSources = (sources = []) => {
  const byKey = new Map();
  sources.forEach((source) => {
    if (!source || typeof source !== "object") return;
    const key = `${source.source}:${source.source_id}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { ...source, key, ...splitTitle(source.title) });
    } else if ((source.score ?? -1) > (existing.score ?? -1)) {
      existing.score = source.score;
    }
  });

  const all = [...byKey.values()];
  const knowledge = all
    .filter((s) => KNOWLEDGE_TYPES.has(s.source))
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  const records = all.filter((s) => !KNOWLEDGE_TYPES.has(s.source));
  return { knowledge, records, total: all.length };
};

const SourceIcon = ({ source, size = 28, accent }) => {
  const Icon = getSourceType(source).icon;
  const isVideo = source.source === "video";
  const tint = isVideo ? "#e5484d" : accent;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: size > 20 ? "8px" : "50%",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: size > 20 ? alpha(tint, 0.1) : "#fff",
        color: tint,
        border: size > 20 ? "none" : `1px solid ${alpha(tint, 0.25)}`,
      }}
    >
      <Icon size={Math.round(size * 0.55)} />
    </Box>
  );
};

/**
 * Botón compacto bajo la respuesta: iconos apilados + "N fuentes", como en
 * Claude. Abre el panel lateral con el detalle.
 */
export const SourcesButton = ({ sources, active, onClick, accent }) => {
  const { knowledge, records, total } = useMemo(
    () => groupSources(sources),
    [sources]
  );
  if (total === 0) return null;

  const preview = [...knowledge, ...records].slice(0, 3);

  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={active}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        height: 26,
        pl: 0.5,
        pr: 1.25,
        borderRadius: "13px",
        border: "1px solid",
        borderColor: active ? alpha(accent, 0.45) : "#e4e4ea",
        bgcolor: active ? alpha(accent, 0.08) : "#fff",
        color: "#4b4f58",
        fontSize: "0.75rem",
        fontWeight: 500,
        transition: "all .15s ease",
        "&:hover": { borderColor: alpha(accent, 0.45) },
      }}
    >
      <Box sx={{ display: "flex" }}>
        {preview.map((source, i) => (
          <Box key={source.key} sx={{ ml: i === 0 ? 0 : "-6px" }}>
            <SourceIcon source={source} size={18} accent={accent} />
          </Box>
        ))}
      </Box>
      {total} {total === 1 ? "fuente" : "fuentes"}
    </ButtonBase>
  );
};

const SourceCard = ({ source, index, onOpen, accent }) => {
  const type = getSourceType(source);
  const external = isExternalUrl(source.url);
  const clickable = Boolean(source.url);
  const meta = [getHostLabel(source.url), source.ref || (!external && type.label)]
    .filter(Boolean)
    .join(" · ");

  return (
    <ButtonBase
      onClick={clickable ? () => onOpen(source.url) : undefined}
      disabled={!clickable}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        p: 1.25,
        textAlign: "left",
        borderRadius: "10px",
        border: "1px solid #ececf1",
        bgcolor: "#fff",
        transition: "border-color .15s ease, box-shadow .15s ease",
        "&:hover": {
          borderColor: alpha(accent, 0.35),
          boxShadow: "0 2px 8px rgba(16,24,40,0.06)",
        },
        "&:hover .source-open": { opacity: 1 },
      }}
    >
      <SourceIcon source={source} accent={accent} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.72rem",
            color: "#8a8f98",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
          noWrap
        >
          {index != null && (
            <Box component="span" sx={{ fontWeight: 600, color: "#6b7079" }}>
              {index}
            </Box>
          )}
          {meta}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 500,
            color: "#23262d",
            lineHeight: 1.35,
            mt: 0.25,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {source.title || type.label}
        </Typography>
      </Box>
      {clickable && (
        <Box
          className="source-open"
          sx={{ color: "#8a8f98", opacity: 0, transition: "opacity .15s", mt: 0.25 }}
        >
          {external ? <ExternalLink size={14} /> : <ChevronRight size={14} />}
        </Box>
      )}
    </ButtonBase>
  );
};

const SectionTitle = ({ children }) => (
  <Typography
    sx={{
      fontSize: "0.72rem",
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: "#8a8f98",
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

/** Panel lateral con las fuentes de un mensaje. */
export const SourcesPanel = ({ message, onClose, onOpenSource, accent }) => {
  const [showAllRecords, setShowAllRecords] = useState(false);
  const { knowledge, records, total } = useMemo(
    () => groupSources(message?.mcp_metadata?.sources),
    [message]
  );

  const visibleRecords = showAllRecords
    ? records
    : records.slice(0, RECORDS_PREVIEW);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fbfbfc",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          minHeight: 64,
          boxSizing: "border-box",
          flexShrink: 0,
          borderBottom: "1px solid #ececf1",
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography fontWeight={600} color="#3a2f64">
            Fuentes
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: "#8a8f98" }}>
            {total}
          </Typography>
        </Box>
        <Tooltip title="Cerrar fuentes">
          <IconButton size="small" onClick={onClose}>
            <X size={18} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {knowledge.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <SectionTitle>Base de conocimiento</SectionTitle>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {knowledge.map((source, i) => (
                <SourceCard
                  key={source.key}
                  source={source}
                  index={i + 1}
                  onOpen={onOpenSource}
                  accent={accent}
                />
              ))}
            </Box>
          </Box>
        )}

        {records.length > 0 && (
          <Box>
            <SectionTitle>En Markezy</SectionTitle>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {visibleRecords.map((source) => (
                <SourceCard
                  key={source.key}
                  source={source}
                  onOpen={onOpenSource}
                  accent={accent}
                />
              ))}
            </Box>
            {records.length > RECORDS_PREVIEW && (
              <ButtonBase
                onClick={() => setShowAllRecords((v) => !v)}
                sx={{
                  mt: 1,
                  px: 1,
                  py: 0.5,
                  borderRadius: "6px",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  color: accent,
                  "&:hover": { bgcolor: alpha(accent, 0.08) },
                }}
              >
                {showAllRecords
                  ? "Ver menos"
                  : `Ver ${records.length - RECORDS_PREVIEW} más`}
              </ButtonBase>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const sourceShape = PropTypes.shape({
  source: PropTypes.string,
  source_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  url: PropTypes.string,
  ref: PropTypes.string,
  key: PropTypes.string,
});

SourceIcon.propTypes = {
  source: sourceShape.isRequired,
  size: PropTypes.number,
  accent: PropTypes.string.isRequired,
};

SourcesButton.propTypes = {
  sources: PropTypes.arrayOf(PropTypes.object),
  active: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  accent: PropTypes.string.isRequired,
};

SourceCard.propTypes = {
  source: sourceShape.isRequired,
  index: PropTypes.number,
  onOpen: PropTypes.func.isRequired,
  accent: PropTypes.string.isRequired,
};

SectionTitle.propTypes = {
  children: PropTypes.node,
};

SourcesPanel.propTypes = {
  message: PropTypes.shape({
    mcp_metadata: PropTypes.shape({
      sources: PropTypes.arrayOf(PropTypes.object),
    }),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenSource: PropTypes.func.isRequired,
  accent: PropTypes.string.isRequired,
};
